/**
 * Trovva — Recruiting CRM with Kanban Drag-and-Drop
 *
 * Stages: Prospect → Contacted → Interview → Hired | Lost
 * Features:
 *   - HTML5 drag-and-drop between pipeline columns
 *   - Email triage: resumes from Gmail/Outlook auto-import to Prospect
 *   - Add recruit form, notes, delete
 *   - Stats bar with conversion metrics
 *   - Forward-to-recruit email address
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Plus, Phone, Mail, Trash2, MessageSquare, X, ChevronRight, ChevronLeft,
  GripVertical, UserPlus, CheckCircle, XCircle, Inbox, Forward, RefreshCw,
  AlertCircle, ArrowRight, Search, Filter, MoreHorizontal, Calendar,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { theme } from '../../lib/theme'

// ── Pipeline stages ──────────────────────────────────────────
const STAGES = ['prospect', 'contacted', 'interview', 'hired', 'lost']

const STAGE_CONFIG = {
  prospect:  { label: 'Prospect',  color: theme.colors.info,    icon: UserPlus },
  contacted: { label: 'Contacted', color: theme.colors.accent,  icon: Phone },
  interview: { label: 'Interview', color: '#8b5cf6',            icon: Calendar },
  hired:     { label: 'Hired',     color: theme.colors.success, icon: CheckCircle },
  lost:      { label: 'Lost',      color: theme.colors.danger,  icon: XCircle },
}

const PIPELINE_STAGES = ['prospect', 'contacted', 'interview'] // active pipeline
const TERMINAL_STAGES = ['hired', 'lost'] // outcome buckets

const SOURCE_OPTIONS = [
  { value: 'referral', label: 'Referral' },
  { value: 'event', label: 'Event' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email_resume', label: 'Email / Resume' },
  { value: 'other', label: 'Other' },
]

const DEMO_DATA = [
  {
    id: '1', first_name: 'Maria', last_name: 'Rodriguez', email: 'maria@example.com',
    phone: '(609) 555-1234', current_brokerage: 'Keller Williams', annual_production: 2500000,
    stage: 'contacted', source: 'referral', notes: 'Met at NJAR conference, strong producer',
    created_at: '2026-04-01', updated_at: '2026-04-15',
  },
  {
    id: '2', first_name: 'James', last_name: 'Chen', email: 'james.chen@example.com',
    phone: '(609) 555-5678', current_brokerage: 'Coldwell Banker', annual_production: 1800000,
    stage: 'interview', source: 'linkedin', notes: 'Interview scheduled for April 22',
    created_at: '2026-04-05', updated_at: '2026-04-18',
  },
  {
    id: '3', first_name: 'Sarah', last_name: 'Thompson', email: 'sarah.t@example.com',
    phone: '(609) 555-9012', current_brokerage: 'RE/MAX', annual_production: 3200000,
    stage: 'interview', source: 'cold_call', notes: 'Great interview, waiting on decision',
    created_at: '2026-03-28', updated_at: '2026-04-10',
  },
  {
    id: '4', first_name: 'David', last_name: 'Williams', email: 'david.w@example.com',
    phone: '(609) 555-3456', current_brokerage: 'Century 21', annual_production: 1500000,
    stage: 'prospect', source: 'event', notes: 'Connected at broker summit',
    created_at: '2026-04-10', updated_at: '2026-04-10',
  },
  {
    id: '5', first_name: 'Jessica', last_name: 'Martinez', email: 'jmartinez@example.com',
    phone: '(609) 555-7890', current_brokerage: "Sotheby's International", annual_production: 4100000,
    stage: 'hired', source: 'referral', notes: 'Signed ICA, starting May 1',
    created_at: '2026-03-15', updated_at: '2026-04-12',
  },
]

// ── Helpers ──────────────────────────────────────────────────
async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null
  return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
}

function getDaysAgo(dateStr) {
  if (!dateStr) return ''
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return '1d ago'
  return `${days}d ago`
}

function formatProduction(val) {
  if (!val) return ''
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`
  return `$${val}`
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function RecruitingCRM({ user, org, showToast }) {
  const [recruits, setRecruits] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedRecruit, setSelectedRecruit] = useState(null)
  const [noteText, setNoteText] = useState('')
  const [showEmailTriage, setShowEmailTriage] = useState(false)
  const [triageEmails, setTriageEmails] = useState([])
  const [scanningEmails, setScanningEmails] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Drag state
  const [draggedId, setDraggedId] = useState(null)
  const [dragOverStage, setDragOverStage] = useState(null)

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    current_brokerage: '', annual_production: '', license_number: '',
    notes: '', source: 'referral',
  })

  // ── Fetch recruits ────────────────────────────────────────
  const fetchRecruits = useCallback(async () => {
    try {
      const h = await getHeaders()
      if (!h) { setRecruits(DEMO_DATA); setLoading(false); return }
      const resp = await fetch('/api/recruiting', { headers: h })
      if (resp.ok) {
        const json = await resp.json()
        const list = json.data || json.prospects || []
        setRecruits(list.length ? list : DEMO_DATA)
      } else {
        setRecruits(DEMO_DATA)
      }
    } catch {
      setRecruits(DEMO_DATA)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchRecruits() }, [fetchRecruits])

  // ── Role check ────────────────────────────────────────────
  if (user?.role === 'agent') {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: theme.colors.textLight }}>
        <p style={{ fontSize: 14 }}>Broker access required</p>
      </div>
    )
  }

  // ── Move stage (API) ──────────────────────────────────────
  const moveToStage = async (recruitId, newStage) => {
    // Optimistic update
    setRecruits(prev => prev.map(r =>
      r.id === recruitId ? { ...r, stage: newStage, updated_at: new Date().toISOString() } : r
    ))

    try {
      const h = await getHeaders()
      if (!h) return
      await fetch(`/api/recruiting?id=${recruitId}`, {
        method: 'PATCH', headers: h,
        body: JSON.stringify({ stage: newStage }),
      })
    } catch {
      // Revert on failure
      fetchRecruits()
    }
  }

  // ── Drag-and-drop handlers ────────────────────────────────
  const handleDragStart = (e, recruitId) => {
    setDraggedId(recruitId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', recruitId)
    // Make ghost slightly transparent
    if (e.target) {
      setTimeout(() => { e.target.style.opacity = '0.4' }, 0)
    }
  }

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1'
    setDraggedId(null)
    setDragOverStage(null)
  }

  const handleDragOver = (e, stage) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverStage(stage)
  }

  const handleDragLeave = (e, stage) => {
    // Only clear if actually leaving the column
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null)
    }
  }

  const handleDrop = (e, stage) => {
    e.preventDefault()
    setDragOverStage(null)
    const recruitId = e.dataTransfer.getData('text/plain') || draggedId
    if (recruitId) {
      const recruit = recruits.find(r => r.id === recruitId || r.id === parseInt(recruitId))
      if (recruit && recruit.stage !== stage) {
        moveToStage(recruit.id, stage)
      }
    }
  }

  // ── Add recruit ───────────────────────────────────────────
  const handleAddRecruit = async () => {
    if (!formData.first_name || !formData.email) {
      showToast?.('Name and email required', 'error')
      return
    }
    try {
      const h = await getHeaders()
      if (!h) { showToast?.('Please log in', 'error'); return }
      const resp = await fetch('/api/recruiting', {
        method: 'POST', headers: h,
        body: JSON.stringify({ ...formData, annual_production: parseInt(formData.annual_production) || 0 }),
      })
      if (resp.ok) {
        showToast?.('Recruit added to pipeline', 'success')
        setShowAddForm(false)
        resetForm()
        fetchRecruits()
      } else {
        const d = await resp.json()
        showToast?.(d.error || 'Failed to add recruit', 'error')
      }
    } catch {
      showToast?.('Failed to add recruit', 'error')
    }
  }

  // ── Notes ─────────────────────────────────────────────────
  const handleAddNote = async (recruitId) => {
    if (!noteText.trim()) return
    try {
      const h = await getHeaders()
      if (!h) return
      const resp = await fetch(`/api/recruiting?id=${recruitId}&action=add_note`, {
        method: 'PATCH', headers: h,
        body: JSON.stringify({ text: noteText }),
      })
      if (resp.ok) {
        fetchRecruits()
        setNoteText('')
        setSelectedRecruit(null)
        showToast?.('Note added', 'success')
      }
    } catch { showToast?.('Failed to add note', 'error') }
  }

  // ── Delete ────────────────────────────────────────────────
  const handleDeleteRecruit = async (recruitId) => {
    if (!confirm('Remove this recruit from the pipeline?')) return
    try {
      const h = await getHeaders()
      if (!h) return
      await fetch(`/api/recruiting?id=${recruitId}`, { method: 'DELETE', headers: h })
      setRecruits(prev => prev.filter(r => r.id !== recruitId))
      showToast?.('Recruit removed', 'success')
    } catch { showToast?.('Failed to remove', 'error') }
  }

  // ── Email triage scan ─────────────────────────────────────
  const scanForResumes = async () => {
    setScanningEmails(true)
    try {
      const h = await getHeaders()
      if (!h) return
      const resp = await fetch('/api/recruiting?action=scan_emails', { headers: h })
      if (resp.ok) {
        const data = await resp.json()
        setTriageEmails(data.emails || [])
        if (data.imported > 0) {
          showToast?.(`${data.imported} new prospect(s) imported from email`, 'success')
          fetchRecruits()
        } else {
          showToast?.('No new recruiting emails found', 'info')
        }
      }
    } catch {
      showToast?.('Email scan failed — check your email integration', 'error')
    }
    setScanningEmails(false)
  }

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '',
      current_brokerage: '', annual_production: '', license_number: '',
      notes: '', source: 'referral',
    })
  }

  // ── Stats ─────────────────────────────────────────────────
  const totalRecruits = recruits.length
  const inPipeline = recruits.filter(r => PIPELINE_STAGES.includes(r.stage)).length
  const hiredCount = recruits.filter(r => r.stage === 'hired').length
  const lostCount = recruits.filter(r => r.stage === 'lost').length
  const conversionRate = (hiredCount + lostCount) > 0
    ? Math.round((hiredCount / (hiredCount + lostCount)) * 100) : 0

  // ── Filter recruits for search ────────────────────────────
  const filterBySearch = (list) => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(r =>
      `${r.first_name} ${r.last_name} ${r.email} ${r.current_brokerage}`.toLowerCase().includes(q)
    )
  }

  // ── Render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: theme.colors.textLight }}>
        Loading recruiting pipeline...
      </div>
    )
  }

  return (
    <div style={{ padding: 24, background: theme.colors.bg, minHeight: '100vh' }}>
      {/* ── Stats Bar ──────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12, marginBottom: 20,
      }}>
        <StatsCard label="Total Recruits" value={totalRecruits} color={theme.colors.primary} />
        <StatsCard label="In Pipeline" value={inPipeline} color={theme.colors.info} />
        <StatsCard label="Hired" value={hiredCount} color={theme.colors.success} />
        <StatsCard label="Lost" value={lostCount} color={theme.colors.danger} />
        <StatsCard label="Win Rate" value={`${conversionRate}%`} color={theme.colors.accent} />
      </div>

      {/* ── Header Bar ─────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, flexWrap: 'wrap', gap: 10,
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
          Recruiting Pipeline
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: theme.radius.sm,
            border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
            minWidth: 160,
          }}>
            <Search size={13} color={theme.colors.textLight} />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search recruits..."
              style={{
                border: 'none', outline: 'none', fontSize: 12,
                color: theme.colors.text, background: 'transparent',
                fontFamily: 'inherit', width: '100%',
              }}
            />
          </div>

          {/* Email Triage button */}
          <button
            onClick={() => setShowEmailTriage(!showEmailTriage)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: theme.radius.sm,
              background: showEmailTriage ? `${theme.colors.info}15` : theme.colors.card,
              border: `1px solid ${showEmailTriage ? theme.colors.info : theme.colors.border}`,
              color: showEmailTriage ? theme.colors.info : theme.colors.text,
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <Inbox size={14} /> Email Triage
          </button>

          {/* Add Recruit button */}
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: theme.radius.sm,
              background: theme.colors.primary, color: '#fff',
              border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Plus size={14} /> Add Recruit
          </button>
        </div>
      </div>

      {/* ── Email Triage Panel ─────────────────────────────── */}
      {showEmailTriage && (
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.lg,
          border: `1px solid ${theme.colors.border}`, padding: 20,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 4px' }}>
                <Inbox size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Email Resume Triage
              </h3>
              <p style={{ fontSize: 13, color: theme.colors.textLight, margin: 0, lineHeight: 1.5 }}>
                Trovva scans your connected Gmail and Outlook for incoming resumes and recruiting inquiries,
                then auto-imports them as Prospects.
              </p>
            </div>
            <button onClick={() => setShowEmailTriage(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={theme.colors.textLight} />
            </button>
          </div>

          {/* Three methods */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            {/* Gmail scan */}
            <div style={{
              padding: 14, borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, background: '#fce8e6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Mail size={14} color="#ea4335" />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Gmail</span>
              </div>
              <p style={{ fontSize: 12, color: theme.colors.textLight, margin: 0, lineHeight: 1.4 }}>
                Scans for emails with resumes, license info, or "interested in joining"
              </p>
            </div>

            {/* Outlook scan */}
            <div style={{
              padding: 14, borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, background: '#e8f0fe',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Mail size={14} color="#0078d4" />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Outlook</span>
              </div>
              <p style={{ fontSize: 12, color: theme.colors.textLight, margin: 0, lineHeight: 1.4 }}>
                Scans Microsoft 365 inbox for recruiting-related emails and attachments
              </p>
            </div>

            {/* Email forwarding */}
            <div style={{
              padding: 14, borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.info}30`, background: `${theme.colors.info}06`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, background: `${theme.colors.info}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Forward size={14} color={theme.colors.info} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Forward</span>
              </div>
              <p style={{ fontSize: 12, color: theme.colors.textLight, margin: 0, lineHeight: 1.4 }}>
                Forward any email to your recruiting inbox and it auto-creates a Prospect
              </p>
            </div>
          </div>

          {/* Forwarding address */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: theme.radius.sm,
            background: `${theme.colors.primary}06`, border: `1px solid ${theme.colors.primary}20`,
            marginBottom: 14,
          }}>
            <Forward size={14} color={theme.colors.primary} />
            <span style={{ fontSize: 12, color: theme.colors.textLight }}>Forward resumes to:</span>
            <code style={{
              fontSize: 12, fontWeight: 600, color: theme.colors.primary,
              background: `${theme.colors.primary}10`, padding: '2px 8px', borderRadius: 4,
            }}>
              recruit@{org?.slug || 'yourbrokerage'}.trovva.ai
            </code>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(`recruit@${org?.slug || 'yourbrokerage'}.trovva.ai`)
                showToast?.('Copied to clipboard', 'success')
              }}
              style={{
                padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer', color: theme.colors.text,
              }}
            >
              Copy
            </button>
          </div>

          {/* Scan button */}
          <button
            onClick={scanForResumes}
            disabled={scanningEmails}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 18px', borderRadius: theme.radius.sm,
              background: theme.colors.primary, color: '#fff',
              border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              opacity: scanningEmails ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} style={scanningEmails ? { animation: 'spin 1s linear infinite' } : {}} />
            {scanningEmails ? 'Scanning...' : 'Scan Inbox Now'}
          </button>

          {/* Inline keyframe for spin */}
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── Kanban Board ───────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 12, overflowX: 'auto',
        paddingBottom: 12, minHeight: 500,
      }}>
        {STAGES.map(stage => {
          const config = STAGE_CONFIG[stage]
          const StageIcon = config.icon
          const stageRecruits = filterBySearch(recruits.filter(r => r.stage === stage))
          const isOver = dragOverStage === stage
          const isTerminal = TERMINAL_STAGES.includes(stage)

          return (
            <div
              key={stage}
              onDragOver={(e) => handleDragOver(e, stage)}
              onDragLeave={(e) => handleDragLeave(e, stage)}
              onDrop={(e) => handleDrop(e, stage)}
              style={{
                flex: isTerminal ? '0 0 220px' : '1 1 240px',
                minWidth: isTerminal ? 200 : 240,
                background: isOver ? `${config.color}08` : theme.colors.card,
                borderRadius: theme.radius.lg,
                border: `1.5px solid ${isOver ? config.color : theme.colors.border}`,
                padding: 0,
                transition: 'border-color 0.15s, background 0.15s',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* Column Header */}
              <div style={{
                padding: '12px 14px', borderBottom: `2px solid ${config.color}30`,
                background: `${config.color}06`, borderRadius: `${theme.radius.lg}px ${theme.radius.lg}px 0 0`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <StageIcon size={14} color={config.color} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
                    {config.label}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
                    background: `${config.color}15`, color: config.color, marginLeft: 'auto',
                  }}>
                    {stageRecruits.length}
                  </span>
                </div>
              </div>

              {/* Cards */}
              <div style={{
                flex: 1, padding: 8, overflowY: 'auto',
                display: 'flex', flexDirection: 'column', gap: 8,
                minHeight: 100,
              }}>
                {stageRecruits.length === 0 && (
                  <div style={{
                    padding: '20px 12px', textAlign: 'center',
                    color: theme.colors.textLight, fontSize: 12, opacity: 0.6,
                  }}>
                    {isOver ? 'Drop here' : 'No recruits'}
                  </div>
                )}

                {stageRecruits.map(recruit => (
                  <RecruitCard
                    key={recruit.id}
                    recruit={recruit}
                    stage={stage}
                    stageConfig={config}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onMoveNext={() => {
                      const idx = STAGES.indexOf(stage)
                      if (idx < STAGES.length - 1 && stage !== 'hired' && stage !== 'lost') {
                        moveToStage(recruit.id, STAGES[idx + 1])
                      }
                    }}
                    onMovePrev={() => {
                      const idx = STAGES.indexOf(stage)
                      if (idx > 0) moveToStage(recruit.id, STAGES[idx - 1])
                    }}
                    onHire={() => moveToStage(recruit.id, 'hired')}
                    onLose={() => moveToStage(recruit.id, 'lost')}
                    onNote={() => { setSelectedRecruit(recruit); setNoteText('') }}
                    onDelete={() => handleDeleteRecruit(recruit.id)}
                    isTerminal={isTerminal}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Add Recruit Modal ──────────────────────────────── */}
      {showAddForm && (
        <Modal onClose={() => setShowAddForm(false)}>
          <div style={{ maxWidth: 500, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: 0 }}>Add Recruit</h2>
              <button onClick={() => setShowAddForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color={theme.colors.textLight} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormRow>
                <FormField label="First Name *" value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })} />
                <FormField label="Last Name" value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
              </FormRow>

              <FormField label="Email *" type="email" value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })} />

              <FormField label="Phone" value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })} />

              <FormRow>
                <FormField label="Current Brokerage" value={formData.current_brokerage}
                  onChange={e => setFormData({ ...formData, current_brokerage: e.target.value })} />
                <FormField label="Annual Production ($)" type="number" value={formData.annual_production}
                  onChange={e => setFormData({ ...formData, annual_production: e.target.value })} />
              </FormRow>

              <FormRow>
                <FormField label="NJ License #" value={formData.license_number}
                  onChange={e => setFormData({ ...formData, license_number: e.target.value })} />
                <FormSelect label="Source" value={formData.source}
                  onChange={e => setFormData({ ...formData, source: e.target.value })}
                  options={SOURCE_OPTIONS} />
              </FormRow>

              <FormField label="Notes" value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })} isTextarea />

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => setShowAddForm(false)}
                  style={{
                    flex: 1, padding: '9px 16px', background: 'transparent',
                    border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
                    cursor: 'pointer', fontSize: 13, color: theme.colors.textLight,
                  }}>
                  Cancel
                </button>
                <button onClick={handleAddRecruit}
                  style={{
                    flex: 1, padding: '9px 16px', background: theme.colors.primary,
                    color: '#fff', border: 'none', borderRadius: theme.radius.sm,
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}>
                  Add to Pipeline
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Note Modal ─────────────────────────────────────── */}
      {selectedRecruit && (
        <Modal onClose={() => setSelectedRecruit(null)}>
          <div style={{ maxWidth: 400, width: '100%' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px' }}>
              Add Note — {selectedRecruit.first_name} {selectedRecruit.last_name}
            </h3>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter note..."
              autoFocus
              style={{
                width: '100%', minHeight: 100, padding: 12,
                border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
                fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                marginBottom: 12, boxSizing: 'border-box', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setSelectedRecruit(null)}
                style={{
                  flex: 1, padding: '8px 14px', background: 'transparent',
                  border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
                  cursor: 'pointer', fontSize: 13,
                }}>Cancel</button>
              <button onClick={() => handleAddNote(selectedRecruit.id)}
                style={{
                  flex: 1, padding: '8px 14px', background: theme.colors.primary,
                  color: '#fff', border: 'none', borderRadius: theme.radius.sm,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>Save Note</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// RECRUIT CARD (draggable)
// ═══════════════════════════════════════════════════════════════
function RecruitCard({
  recruit, stage, stageConfig, onDragStart, onDragEnd,
  onMoveNext, onMovePrev, onHire, onLose, onNote, onDelete, isTerminal,
}) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, recruit.id)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: theme.colors.bg,
        border: `1px solid ${hovered ? stageConfig.color + '60' : theme.colors.border}`,
        borderRadius: theme.radius.md,
        padding: 12,
        cursor: 'grab',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: hovered ? `0 2px 8px ${stageConfig.color}15` : 'none',
        position: 'relative',
      }}
    >
      {/* Drag handle hint */}
      <div style={{
        position: 'absolute', top: 6, right: 6, opacity: hovered ? 0.4 : 0,
        transition: 'opacity 0.15s',
      }}>
        <GripVertical size={12} color={theme.colors.textLight} />
      </div>

      {/* Name */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
          {recruit.first_name} {recruit.last_name}
        </div>
        {recruit.current_brokerage && (
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>{recruit.current_brokerage}</div>
        )}
      </div>

      {/* Production */}
      {recruit.annual_production > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.success, marginBottom: 6 }}>
          {formatProduction(recruit.annual_production)}
        </div>
      )}

      {/* Contact info */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6,
        paddingBottom: 6, borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        {recruit.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Phone size={10} color={theme.colors.textLight} />
            <span style={{ fontSize: 11, color: theme.colors.textLight }}>{recruit.phone}</span>
          </div>
        )}
        {recruit.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Mail size={10} color={theme.colors.textLight} />
            <span style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {recruit.email}
            </span>
          </div>
        )}
      </div>

      {/* Time + notes */}
      <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 6 }}>
        {getDaysAgo(recruit.updated_at)}
        {recruit.source === 'email_resume' && (
          <span style={{
            marginLeft: 6, fontSize: 10, fontWeight: 600,
            padding: '1px 5px', borderRadius: 4,
            background: `${theme.colors.info}12`, color: theme.colors.info,
          }}>
            via Email
          </span>
        )}
      </div>

      {recruit.notes && (
        <div style={{
          fontSize: 11, color: theme.colors.textLight, fontStyle: 'italic',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          marginBottom: 8,
        }}>
          "{typeof recruit.notes === 'string' ? recruit.notes.split('\n')[0] : recruit.notes[recruit.notes.length - 1]?.text || ''}"
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex', gap: 4, paddingTop: 6,
        borderTop: `1px solid ${theme.colors.border}`,
      }}>
        {/* Move buttons — only for pipeline stages */}
        {!isTerminal && (
          <>
            {/* Quick outcome buttons */}
            <button onClick={onHire} title="Mark as Hired"
              style={{
                padding: '5px 8px', borderRadius: 6,
                background: `${theme.colors.success}10`, border: `1px solid ${theme.colors.success}30`,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                fontSize: 10, fontWeight: 600, color: theme.colors.success,
              }}>
              <CheckCircle size={11} /> Hire
            </button>
            <button onClick={onLose} title="Mark as Lost"
              style={{
                padding: '5px 8px', borderRadius: 6,
                background: `${theme.colors.danger}08`, border: `1px solid ${theme.colors.danger}20`,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                fontSize: 10, fontWeight: 600, color: theme.colors.danger,
              }}>
              <XCircle size={11} /> Lost
            </button>
          </>
        )}

        {/* Back to pipeline from terminal */}
        {isTerminal && (
          <button onClick={onMovePrev} title="Move back to pipeline"
            style={{
              padding: '5px 8px', borderRadius: 6,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 500, color: theme.colors.text,
            }}>
            <ChevronLeft size={11} /> Back
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Note + Delete */}
        <button onClick={onNote} title="Add note"
          style={{
            padding: 5, borderRadius: 6, background: theme.colors.card,
            border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
          }}>
          <MessageSquare size={12} color={theme.colors.textLight} />
        </button>
        <button onClick={onDelete} title="Remove"
          style={{
            padding: 5, borderRadius: 6, background: theme.colors.card,
            border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
          }}>
          <Trash2 size={12} color={theme.colors.danger} />
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════
function StatsCard({ label, value, color }) {
  return (
    <div style={{
      background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.lg, padding: '14px 16px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || theme.colors.primary }}>{value}</div>
    </div>
  )
}

function FormField({ label, value, onChange, type = 'text', isTextarea = false }) {
  const style = {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
    fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
  }
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: theme.colors.text }}>{label}</span>
      {isTextarea
        ? <textarea value={value} onChange={onChange} style={{ ...style, resize: 'vertical', minHeight: 70 }} />
        : <input type={type} value={value} onChange={onChange} style={style} />
      }
    </label>
  )
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: theme.colors.text }}>{label}</span>
      <select value={value} onChange={onChange} style={{
        padding: '8px 10px', border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.radius.sm, fontSize: 13, fontFamily: 'inherit',
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

function FormRow({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>
}

function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.colors.card, borderRadius: theme.radius.lg,
          padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  )
}
